import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Button from '@material-ui/core/Button';
// Общий стиль статей: каждая берёт из него только нужные классы
// eslint-disable-next-line css-modules/no-unused-class
import s from '../../components/Article/Article.css';
import StagedLink from '../../components/StagedLink';
import { CHROME_STORE_URL } from '../../utils/seo';

/**
 * /how-to-change-new-tab
 *
 * Информационная страница под запрос «how to change the chrome new tab page».
 * Правило: сначала честно отвечаем на вопрос, включая варианты без нашего
 * расширения. Страница, которая сразу продаёт, не ранжируется и не читается.
 */
function HowTo() {
	return (
		<div className={s.root}>
			<p className={s.lead}>
				There are two honest answers, and which one you need depends on
				how far you want to go. Chrome can change the picture and the
				shortcuts on its own page in about thirty seconds. Replacing the
				page with something else entirely needs an extension: a clock, a
				countdown, your task list. Below is both, plus how to undo
				either one.
			</p>

			<h2>What Chrome lets you change on its own</h2>

			<p>
				Less than most people expect. The built-in options cover the
				background image, the shortcut tiles, and a colour theme. The
				layout itself is fixed: search box in the middle, tiles
				underneath, and no way to add anything that’s not a shortcut.
			</p>

			<ol>
				<li>Open a new tab.</li>
				<li>
					Click <b>Customise Chrome</b> in the bottom right corner. A
					pencil icon on older versions.
				</li>
				<li>
					<b>Appearance</b> sets a background: one of Google&rsquo;s
					collections, a solid colour, or an image you upload. (The
					background alone has its{' '}
					<StagedLink to="/how-to-change-chrome-background">
						own guide
					</StagedLink>
					, GIFs and video included.)
				</li>
				<li>
					<b>Shortcuts</b> switches the tiles between your most
					visited sites and ones you choose by hand, or hides them
					altogether.
				</li>
				<li>
					<b>Cards</b> turns on the occasional suggestion panel, if
					you want it.
				</li>
			</ol>

			<div className={s.note}>
				If all you wanted was to stop seeing the most-visited tiles,
				this is the whole job. <b>Shortcuts → Hide shortcuts</b> and you
				are done, no extension needed.
			</div>

			<h2>What Chrome doesn’t let you do</h2>

			<p>
				You can’t set the new tab page to a specific website. This
				surprises people, because Chrome does have a homepage setting
				and it does accept a URL, but the homepage is the button next to
				the address bar, not the new tab. They are separate, and only
				one of them takes a URL.
			</p>

			<p>
				You also can’t add anything to the page. No clock, no notes, no
				calendar, no to-do list. The built-in page has one layout and it
				isn’t adjustable. That’s the point at which everything else on
				this page starts.
			</p>

			<h2>Replacing the page with an extension</h2>

			<p>
				Chrome lets an extension take over the new tab entirely. Install
				one and your next new tab is whatever that extension draws
				instead. There are a lot of them, they vary enormously, and they
				fall into roughly four groups:
			</p>

			<div className={s.tableWrap}>
				<table className={s.table}>
					<thead>
						<tr>
							<th>Kind</th>
							<th>What you get</th>
							<th>Suits you if</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>Photo and focus pages</td>
							<td>
								A full-screen photograph, a greeting, often a
								single task for the day
							</td>
							<td>
								You want the page calmer than it is now, not
								busier
							</td>
						</tr>
						<tr>
							<td>Minimal pages</td>
							<td>A clock, a search box, very little else</td>
							<td>You find the default page distracting</td>
						</tr>
						<tr>
							<td>Launchers and speed dials</td>
							<td>A grid of site icons you arrange</td>
							<td>
								You open the same twenty sites and want them one
								click away
							</td>
						</tr>
						<tr>
							<td>Dashboards</td>
							<td>
								Widgets you place yourself, clocks, lists,
								countdowns, notes, weather
							</td>
							<td>
								You want the page to carry information, not just
								links
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<figure className={s.figure}>
				<img
					src="/img/widgets/template-picker.webp"
					srcSet="/img/widgets/template-picker-720.webp 720w, /img/widgets/template-picker.webp 1440w"
					sizes="(max-width: 800px) 100vw, 760px"
					alt="First run screen Choose a theme with a Build your own page card above a grid of built-in themes"
					width={1440}
					height={900}
					loading="lazy"
				/>
				<figcaption>
					First run: pick a ready theme or build the page step by step
				</figcaption>
			</figure>

			<figure className={s.figure}>
				<img
					src="/img/widgets/theme-builder-widgets.webp"
					srcSet="/img/widgets/theme-builder-widgets-720.webp 720w, /img/widgets/theme-builder-widgets.webp 1440w"
					sizes="(max-width: 800px) 100vw, 760px"
					alt="Page builder step 1 with six widgets ticked and a live preview of the page on the right"
					width={1440}
					height={900}
					loading="lazy"
				/>
				<figcaption>
					Tick what you need; the preview on the right arranges it as
					you go
				</figcaption>
			</figure>

			<h3>Installing one</h3>

			<ol>
				<li>Open its page in the Chrome Web Store.</li>
				<li>
					Click <b>Add to Chrome</b>, then <b>Add extension</b> in the
					dialogue.
				</li>
				<li>
					Chrome shows a notice saying the new tab page has changed
					and offers to keep or revert it. Choose <b>Keep it</b>.
				</li>
				<li>Open a new tab. That’s the new page.</li>
			</ol>

			<h3>Before you install anything</h3>

			<p>
				The new tab page is valuable to the people who make extensions
				for it, and a minority take advantage of that. Two things are
				worth checking, and they take under a minute:
			</p>

			<ul>
				<li>
					<b>Does it change your search engine?</b> A new tab
					extension has no business touching what your address bar
					searches with. If the listing mentions changing your default
					search, that’s what it’s for.
				</li>
				<li>
					<b>What permissions does it ask for?</b> Reading your
					browsing history and bookmarks is reasonable for a widget
					that displays them, and unreasonable for a wallpaper
					extension. The store lists permissions before you install.
				</li>
			</ul>

			<figure className={s.figure}>
				<img
					src="/img/widgets/edit-mode-panel.webp"
					srcSet="/img/widgets/edit-mode-panel-720.webp 720w, /img/widgets/edit-mode-panel.webp 1440w"
					sizes="(max-width: 800px) 100vw, 760px"
					alt="Edit mode with the widget panel open on the left; the Common group shows News, Interesting facts and Chess puzzle tiles"
					width={1440}
					height={900}
					loading="lazy"
				/>
				<figcaption>
					Open edit mode, pick a group, click a widget to add it
				</figcaption>
			</figure>

			<h2>Changing it back</h2>

			<p>
				Whatever you install, undoing it is the same three steps, and
				nothing about your bookmarks or history is affected:
			</p>

			<ol>
				<li>
					Open <b>chrome://extensions</b>, or Menu → Extensions →
					Manage Extensions.
				</li>
				<li>
					Find the extension and click <b>Remove</b>, or the toggle to
					disable it without deleting it.
				</li>
				<li>
					The next new tab is Chrome&rsquo;s own page again,
					immediately.
				</li>
			</ol>

			<p>
				If a new tab page appeared that you didn’t install, remove it
				the same way and then check <b>Settings → Search engine</b> —
				that’s the setting the less honest ones change while you’re
				looking at the pretty wallpaper.
			</p>

			<h2>Edge, Opera, Brave and Vivaldi</h2>

			<p>
				All four are built on the same engine as Chrome and install
				Chrome extensions, so everything above applies with small
				differences in wording. Edge may ask you to allow extensions
				from other stores the first time. Opera needs its{' '}
				<i>Install Chrome Extensions</i> add-on before the Chrome Web
				Store works. Brave and Vivaldi install them directly, with no
				extra step.
			</p>

			<p>
				Firefox is the exception: it has its own add-on store, and a
				Chrome extension can’t be installed into it. Many new tab
				extensions publish a Firefox build separately.
			</p>

			<h2>Where New Tab Studio fits</h2>

			<p>
				This is our extension, so treat the rest of this section
				accordingly.
			</p>

			<p>
				New Tab Studio is in the fourth group above, the dashboard kind.
				You get a blank page and{' '}
				<StagedLink to="/widgets">a catalogue of widgets</StagedLink> to
				put on it:{' '}
				<StagedLink to="/widgets/countdown">countdowns</StagedLink>,{' '}
				<StagedLink to="/widgets/clock">clocks</StagedLink>,{' '}
				<StagedLink to="/widgets/todo-list">task lists</StagedLink>,{' '}
				<StagedLink to="/widgets/notes">notes</StagedLink>,{' '}
				<StagedLink to="/widgets/weather">weather</StagedLink>,{' '}
				<StagedLink to="/widgets/habit-tracker">
					habit trackers
				</StagedLink>
				. They are dragged into place rather than dropped into a grid,
				which is the main thing that separates it from the others.
			</p>

			<p>
				It doesn’t change your search engine. There’s even a search
				widget setting that deliberately hands queries to whatever
				engine you’ve already chosen. Your layout is stored in your
				browser rather than uploaded, and no account is needed to use
				it.
			</p>

			<p>
				It’s the wrong choice if you want the page simpler than the
				default rather than richer. A free-form canvas takes a few
				minutes to arrange, and if what you actually want is a photo and
				a clock, something minimal will make you happier with less
				effort. That’s a real answer to &ldquo;which one should I
				use&rdquo;, and it’s not always ours.
			</p>

			<div className={s.cta}>
				<p>
					Free, no account, and removable in three clicks if it’s not
					for you.
				</p>
				<Button
					href={CHROME_STORE_URL}
					color="primary"
					variant="contained"
				>
					Add New Tab Studio to Chrome
				</Button>
			</div>

			<h2>Common questions</h2>

			<h3>How do I get my old Chrome new tab page back?</h3>
			<p>
				If an extension replaced it, switch that extension off or remove
				it at <b>chrome://extensions</b>, and Chrome’s own page is back
				on the next tab. If Chrome’s page only looks different after an
				update, open <b>Customize Chrome</b>: Classic Chrome under
				Change theme drops the picture, and the shortcuts switch brings
				the tiles back.
			</p>

			<h3>Can you customize the new tab page in Chrome?</h3>
			<p>
				Partly. Chrome’s own page lets you change the background, the
				colour and the shortcut tiles from the Customize Chrome button,
				while the layout stays fixed. A clock, a to-do list or a
				countdown on that page needs a new tab extension.
			</p>

			<h3>What’s the best way to organize the new tab page?</h3>
			<p>
				Keep it to what you reach for every day: a few shortcuts, a
				search box and whatever you need to see before you start. In New
				Tab Studio people often split it into{' '}
				<StagedLink to="/widgets/workspaces">workspaces</StagedLink>,
				one for work and one for home, so each page stays short.
			</p>

			<h3>Can I set my new tab to open a specific website?</h3>
			<p>
				Not with a Chrome setting. The homepage setting takes a URL; the
				new tab doesn’t. An extension can do it, several exist for
				exactly this, but Chrome on its own can’t.
			</p>

			<h3>Will changing the new tab page slow Chrome down?</h3>
			<p>
				A well-built one, no. The page renders locally. What costs time
				is a page that loads a remote service on every tab. An embedded
				dashboard, a news feed, a heavy background fetched fresh each
				time.
			</p>

			<h3>Do I lose my bookmarks or history?</h3>
			<p>
				No. An extension draws a different page; it doesn’t touch your
				profile. Removing it puts the original page back exactly as it
				was.
			</p>

			<h3>Why did my new tab change on its own?</h3>
			<p>
				Something installed an extension, usually bundled with other
				software. Open <b>chrome://extensions</b>, remove anything you
				don’t recognise, then check your default search engine in
				settings.
			</p>

			<h3>
				Can I have different new tab pages for work and personal use?
			</h3>
			<p>
				Chrome profiles are the general answer, each profile keeps its
				own extensions and settings. Some dashboard extensions also
				offer{' '}
				<StagedLink to="/widgets/workspaces">
					several pages within one
				</StagedLink>
				, which is lighter than switching profiles.
			</p>
		</div>
	);
}

export default withStyles(s)(HowTo);
